import React, { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
interface CountdownTimerProps {
  className?: string;
  theme?: 'dark' | 'light';
  storageKey?: string;
  durationMs?: number;
  onExpire?: () => void;
  expiredMessage?: string;
}
export function CountdownTimer({
  className = '',
  theme = 'dark',
  storageKey = 'cenas_offer_end_time',
  durationMs = 24 * 60 * 60 * 1000,
  onExpire,
  expiredMessage = 'OFERTA EXPIRADA'
}: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [isExpired, setIsExpired] = useState(false);
  useEffect(() => {
    const storedEndTime = localStorage.getItem(storageKey);
    let endTime: number;
    if (storedEndTime) {
      endTime = parseInt(storedEndTime, 10);
    } else {
      endTime = new Date().getTime() + durationMs;
      localStorage.setItem(storageKey, endTime.toString());
    }
    const updateTimer = () => {
      const now = new Date().getTime();
      const distance = endTime - now;
      if (distance < 0) {
        if (!isExpired) {
          setIsExpired(true);
          setTimeLeft({
            hours: 0,
            minutes: 0,
            seconds: 0
          });
          onExpire?.();
        }
        return;
      }
      const hours = Math.floor(distance / (1000 * 60 * 60));
      const minutes = Math.floor(distance % (1000 * 60 * 60) / (1000 * 60));
      const seconds = Math.floor(distance % (1000 * 60) / 1000);
      setTimeLeft({
        hours,
        minutes,
        seconds
      });
    };
    updateTimer();
    const intervalId = setInterval(updateTimer, 1000);
    return () => clearInterval(intervalId);
  }, [storageKey, durationMs]);
  // Check on mount if already expired
  useEffect(() => {
    const storedEndTime = localStorage.getItem(storageKey);
    if (storedEndTime) {
      const endTime = parseInt(storedEndTime, 10);
      if (new Date().getTime() > endTime) {
        setIsExpired(true);
        onExpire?.();
      }
    }
  }, [storageKey]);
  if (isExpired) {
    return <div className={`text-center font-bold text-xl py-4 px-6 rounded-lg border-2 border-urgency text-urgency bg-red-50 ${className}`} data-id="element-46">
        {expiredMessage}
      </div>;
  }
  const formatNumber = (num: number) => num.toString().padStart(2, '0');
  const bgClass = theme === 'dark' ? 'bg-dark text-white' : 'bg-urgency/10 text-urgency';
  const blockClass = theme === 'dark' ? 'bg-dark-light' : 'bg-white shadow-sm';
  const labelClass = theme === 'dark' ? 'text-gray-400' : 'text-urgency/70';
  // Determine whether to show hours
  const showHours = durationMs > 60 * 60 * 1000 || timeLeft.hours > 0;
  return <div className={`flex flex-col items-center ${className}`} data-id="element-47">
      <div className="flex items-center gap-2 mb-3 text-sm font-semibold uppercase tracking-wider text-urgency" data-id="element-48">
        <Clock size={16} className="animate-pulse" data-id="element-49" />
        <span data-id="element-50">La oferta desaparece en:</span>
      </div>

      <div className={`flex items-center gap-2 md:gap-4 p-3 md:p-4 rounded-xl ${bgClass}`} data-id="element-51">
        {showHours && <>
            <div className="flex flex-col items-center" data-id="element-52">
              <div className={`w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-lg text-2xl md:text-3xl font-bold font-serif ${blockClass}`} data-id="element-53">
                {formatNumber(timeLeft.hours)}
              </div>
              <span className={`text-[10px] md:text-xs mt-1 uppercase font-semibold ${labelClass}`} data-id="element-54">
                Horas
              </span>
            </div>
            <div className="text-2xl md:text-3xl font-bold pb-4" data-id="element-55">:</div>
          </>}

        <div className="flex flex-col items-center" data-id="element-56">
          <div className={`w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-lg text-2xl md:text-3xl font-bold font-serif ${blockClass}`} data-id="element-57">
            {formatNumber(timeLeft.minutes)}
          </div>
          <span className={`text-[10px] md:text-xs mt-1 uppercase font-semibold ${labelClass}`} data-id="element-58">
            Minutos
          </span>
        </div>

        <div className="text-2xl md:text-3xl font-bold pb-4" data-id="element-59">:</div>

        <div className="flex flex-col items-center" data-id="element-60">
          <div className={`w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-lg text-2xl md:text-3xl font-bold font-serif ${blockClass}`} data-id="element-61">
            {formatNumber(timeLeft.seconds)}
          </div>
          <span className={`text-[10px] md:text-xs mt-1 uppercase font-semibold ${labelClass}`} data-id="element-62">
            Segundos
          </span>
        </div>
      </div>
    </div>;
}